import React, { useEffect, useState } from 'react'
import HeaderBanner from '../ui/HeaderBanner'
import client from '../../client/Client'

interface Content {
  IEEETemplate: string
  PDFExpressSubmission: {
    asset: {
      _ref: string
    }
  }
}

const Submissions = () => {
  const [EDAS, setEDAS] = useState<string>('')
  const [submissionContent, setSubmissionContent] = useState<Content[]>([])

  useEffect(() => {
    client.fetch('*[_type == "EDAS"]').then((response) => {
      if (response.length > 0) {
        setEDAS(response[0].EDAS)
      }
    })
  }, [])

  useEffect(() => {
    client.fetch('*[_type == "Submissions"]').then((response) => {
      if (response.length > 0) {
        setSubmissionContent(response)
      }
    })
  }, [])

  const getPDFUrl = (submission: Content) => {
    if (!submission?.PDFExpressSubmission?.asset?._ref) return ''

    return `https://cdn.sanity.io/files/79vgbaej/production/${submission.PDFExpressSubmission.asset._ref.slice(
      5,
      -4
    )}.${submission.PDFExpressSubmission.asset._ref.slice(-3)}`
  }

  const hasSubmissionContent = submissionContent.length > 0

  const stepItems = [
    {
      title: 'Create or Log into an EDAS Account',
      items: [
        'Go to the EDAS Link Provided.',
        'If you already have an account, log in with your credentials.',
        'If you are a new user, click "Create a new account" and fill in your name, affiliation, email address, and password.',
        'After creating the account, confirm your email if required.',
      ],
    },
    {
      title: 'Start a New Submission',
      items: [
        'Select the track and conference: SAC, PSE, SCI, or CSP.',
        'Click "Submit".',
      ],
    },
    {
      title: 'Enter Paper Information',
      items: [
        'Fill in the title, abstract, and keywords.',
        'Add all authors with full names, emails, and affiliations.',
        'Ensure the author order matches the first page of the paper.',
        'The submitting author must also be listed as one of the authors.',
      ],
    },
    {
      title: 'Upload Your Manuscript',
      items: [
        'Click "Upload manuscript".',
        'The file must be in PDF format.',
        'Make sure the file follows the IEEE template.',
        'EDAS will check fonts, page size, and margins.',
        'If errors appear, fix them and re-upload.',
      ],
    },
    {
      title: 'Check Submission Confirmation',
      items: [
        'After upload, your paper will appear in "My Papers" on EDAS.',
        'You should also receive an email confirmation.',
      ],
    },
    {
      title: 'Revise if Needed Before Deadline',
      items: [
        'You can update the title, authors, abstract, and PDF before the submission deadline.',
        'After the deadline, changes are usually not allowed.',
      ],
    },
    {
      title: 'After Acceptance',
      items: [
        'Revise the paper according to reviewers’ comments.',
        'Generate an IEEE PDF eXpress compliant file.',
        'Upload the final camera-ready paper on EDAS.',
        'Sign the IEEE Copyright Form in EDAS.',
        'Register for the conference and complete registration payment.',
      ],
    },
  ]

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-32 pb-24">
      <div
        className="fixed left-1/2 transform -translate-x-1/2 bottom-0 pointer-events-none -z-1"
        aria-hidden="true"
      >
        <svg
          width="1360"
          height="578"
          viewBox="0 0 1360 578"
          xmlns="http://www.w3.org/2000/svg"
        >
          <defs>
            <linearGradient
              x1="50%"
              y1="0%"
              x2="50%"
              y2="100%"
              id="illustration-01"
            >
              <stop stopColor="#FFF" offset="0%" />
              <stop stopColor="#EAEAEA" offset="77.402%" />
              <stop stopColor="#DFDFDF" offset="100%" />
            </linearGradient>
          </defs>
          <g fill="url(#illustration-01)" fillRule="evenodd">
            <circle cx="1232" cy="128" r="128" />
            <circle cx="155" cy="443" r="64" />
          </g>
        </svg>
      </div>

      <HeaderBanner title="Submissions" />

      <div className="space-y-8 pt-12">
        {/* Submission Intro */}
        <section className="overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm">
          <div className="grid gap-6 p-6 sm:p-8 lg:grid-cols-[1.15fr_0.85fr] lg:items-center">
            <div>
              <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-red-100 bg-red-50 px-4 py-2">
                <span className="h-2 w-2 rounded-full bg-red-500" />
                <span className="text-xs font-bold uppercase tracking-wide text-red-500">
                  Paper Submission
                </span>
              </div>

              <h1 className="text-3xl font-extrabold tracking-tight text-[#00094f] sm:text-4xl">
                Submit Your Paper Through EDAS
              </h1>

              <p className="mt-4 max-w-2xl text-base leading-7 text-gray-600">
                Paper submissions will be managed through the EDAS conference management
                system. Please prepare your manuscript according to the IEEE format before
                submitting.
              </p>
            </div>

            <div className="rounded-2xl border border-gray-100 bg-gray-50 p-5 shadow-sm">
              <p className="text-sm font-bold uppercase tracking-wide text-gray-500">
                Submission Platform
              </p>

              <h2 className="mt-2 text-xl font-bold text-red-500">
                EDAS Website
              </h2>

              <p className="mt-3 text-sm leading-6 text-gray-600">
                Use the official EDAS link to create or manage your submission.
              </p>

              {EDAS && (
                <a
                  href={EDAS}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-5 inline-flex w-full items-center justify-center rounded-xl bg-red-800 px-5 py-3 text-sm font-bold text-white transition duration-200 hover:bg-red-500 focus:outline-none focus:ring-2 focus:ring-red-400 focus:ring-offset-2"
                >
                  Open EDAS Website
                </a>
              )}
            </div>
          </div>
        </section>

        {/* Paper Format */}
        <section className="rounded-2xl border border-gray-100 bg-white p-6 shadow-sm sm:p-8">
          <div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
            <div className="max-w-2xl">
              <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-blue-100 bg-blue-50 px-4 py-2">
                <span className="h-2 w-2 rounded-full bg-[#00094f]" />
                <span className="text-xs font-bold uppercase tracking-wide text-[#00094f]">
                  Paper Format
                </span>
              </div>

              <h2 className="text-2xl font-extrabold tracking-tight text-[#00094f] sm:text-3xl">
                IEEE Submission Format
              </h2>

              <p className="mt-4 text-base leading-7 text-gray-600">
                Submitted papers should be written in English and limited to six pages
                including figures, tables, and references. Paper format and styles are
                available for download below.
              </p>
            </div>

            {hasSubmissionContent && (
              <div className="grid w-full gap-4 sm:grid-cols-2 lg:max-w-md">
                {submissionContent[0]?.IEEETemplate && (
                  <a
                    href={submissionContent[0].IEEETemplate}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group rounded-2xl border border-gray-100 bg-gray-50 p-5 text-center shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-red-100 hover:bg-white hover:shadow-md"
                  >
                    <div className="mx-auto mb-3 flex h-11 w-11 items-center justify-center rounded-xl bg-red-50 text-red-500">
                      <svg
                        className="h-5 w-5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={2}
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M12 11V3m0 8l-3-3m3 3l3-3M5 13v6h14v-6"
                        />
                      </svg>
                    </div>

                    <span className="font-bold text-[#00094f] transition-colors duration-200 group-hover:text-red-500">
                      IEEE Templates
                    </span>
                  </a>
                )}

                {getPDFUrl(submissionContent[0]) && (
                  <a
                    href={getPDFUrl(submissionContent[0])}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group rounded-2xl border border-gray-100 bg-gray-50 p-5 text-center shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-red-100 hover:bg-white hover:shadow-md"
                  >
                    <div className="mx-auto mb-3 flex h-11 w-11 items-center justify-center rounded-xl bg-[#00094f]/5 text-[#00094f]">
                      <svg
                        className="h-5 w-5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={2}
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M7 21h10a2 2 0 002-2V7l-5-5H7a2 2 0 00-2 2v15a2 2 0 002 2z"
                        />
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M14 2v5h5"
                        />
                      </svg>
                    </div>

                    <span className="font-bold text-[#00094f] transition-colors duration-200 group-hover:text-red-500">
                      PDF Express Submission
                    </span>
                  </a>
                )}
              </div>
            )}
          </div>
        </section>

        {/* Submission Steps */}
        <section className="rounded-2xl border border-gray-100 bg-white p-6 shadow-sm sm:p-8">
          <div className="mb-8">
            <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-blue-100 bg-blue-50 px-4 py-2">
              <span className="h-2 w-2 rounded-full bg-[#00094f]" />
              <span className="text-xs font-bold uppercase tracking-wide text-[#00094f]">
                EDAS Process
              </span>
            </div>

            <h2 className="text-2xl font-extrabold tracking-tight text-[#00094f] sm:text-3xl">
              Submission Steps
            </h2>

            <p className="mt-3 text-base leading-7 text-gray-600">
              Follow these steps carefully to submit your manuscript and complete the
              final submission process after acceptance.
            </p>
          </div>

          <div className="space-y-5">
            {stepItems.map((step, index) => (
              <div
                key={index}
                className="relative rounded-2xl border border-gray-100 bg-gray-50 p-5 shadow-sm transition-all duration-300 hover:border-red-100 hover:bg-white hover:shadow-md sm:p-6"
              >
                <div className="flex gap-4">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-red-500 text-sm font-extrabold text-white shadow-sm">
                    {index + 1}
                  </div>

                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-[#00094f]">
                      {step.title}
                    </h3>

                    <ul className="mt-3 space-y-2 text-sm leading-6 text-gray-700 sm:text-base">
                      {step.items.map((item, itemIndex) => (
                        <li key={itemIndex} className="flex gap-2">
                          <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-red-500" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Help */}
        <section className="rounded-2xl border border-red-100 bg-red-50 p-6 shadow-sm sm:p-8">
          <div className="flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-sm font-bold uppercase tracking-wide text-red-500">
                Need Help?
              </p>

              <h3 className="mt-1 text-2xl font-extrabold text-[#00094f]">
                Technical Support Available
              </h3>

              <p className="mt-2 max-w-2xl text-gray-700">
                If you face any difficulties during the submission of your paper,
                please contact the organizing committee.
              </p>
            </div>

            <a
              className="inline-flex items-center justify-center rounded-lg bg-red-800 px-5 py-3 text-sm font-bold text-white transition duration-200 hover:bg-red-500 focus:outline-none focus:ring-2 focus:ring-red-400 focus:ring-offset-2"
              href="mailto:info@ssd-multiconf.org"
            >
              info@ssd-multiconf.org
            </a>
          </div>
        </section>
      </div>
    </div>
  )
}

export default Submissions